import { useEffect, useState, Fragment, useMemo, useRef, Dispatch, KeyboardEvent, Ref, SetStateAction } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshButton from './RefreshButton';
import { Item } from '../util/Item';
import { Util } from '../util/Util';
import { useTranslation } from 'react-i18next';
import { parseMoneyInput } from '../util/quantity';
import { selectOnFocus } from '../util/focusField';

interface Props<T extends Item> {
    fetchPrices: () => void;
    prices: any[];
    selectedItem: T | null;
    setSelectedItem: Dispatch<SetStateAction<T | null>>;
    itemConstructor: new (data: Partial<T>) => T; // Constructor for T
    isPriceLoading: boolean;
    label?: string;
    inputRef?: Ref<HTMLInputElement>;
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
    /** Shown but not typed into: the server sets this value (a read-only field). */
    disabled?: boolean;
}

const AsynchronousPrice = <T extends Item>({ fetchPrices, prices, selectedItem, setSelectedItem, itemConstructor, isPriceLoading, label, inputRef, onKeyDown, disabled = false }: Props<T>) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<readonly any[]>([]);

    const itemRef = useRef<any>(null);

    const handleOpen = () => {
        setOpen(true);
        if (itemRef.current?.id !== selectedItem?.id) {
            setOptions([]);
            fetchPrices();
        }
    };

    useEffect(() => {
        if (prices.length > 0) {
            setOptions(prices);
        } else {
            setOptions([]);
        }
    }, [prices]);

    const handleClose = () => {
        itemRef.current = selectedItem;
        setOpen(false);
    };

    // Inflate current selected value into options if missing
    const displayedOptions = useMemo(() => {
        if (selectedItem && !options.some(opt => opt.value === selectedItem.price)) {
            return [...options, { value: selectedItem.price, label: selectedItem.price.toString() }];
        }
        return options;
    }, [options, selectedItem]);

    return (
        <Autocomplete
            id="amountInput"
            size="small"
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            options={options}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
            isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                if (typeof option === 'string' || typeof value === 'string') return option === value;
                return option.value === value.value;
            }}
            value={selectedItem ? { value: selectedItem.price, label: selectedItem.price.toString() } : null}
            freeSolo
            onChange={(_, newValue) => {
                if (!newValue || typeof newValue === "string") {
                    const parsed = parseMoneyInput(newValue as string);
                    if (parsed === null) return;
                    setSelectedItem((prev: any) => prev ? new itemConstructor({ ...prev, price: parsed }) : null);
                } else if (typeof newValue === "object" && 'value' in newValue) {
                    setSelectedItem((prev: any) => prev ? new itemConstructor({ ...prev, price: newValue.value }) : null);
                }
            }}
            onInputChange={(_, newInputValue, reason) => {
                if (reason !== "input" || !selectedItem) return;
                const parsed = parseMoneyInput(newInputValue);
                if (parsed === null) return;
                setSelectedItem((prev: any) => prev ? new itemConstructor({ ...prev, price: parsed }) : null);
            }}
            loading={isPriceLoading}
            disabled={disabled}
            autoHighlight
            autoComplete
            autoSelect
            selectOnFocus
            onKeyDown={onKeyDown}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputRef={inputRef}
                    onFocus={selectOnFocus}
                    variant="filled"
                    label={
                        <span>
                            {label || t('common:ui.labels.amount')}&nbsp;&nbsp;
                            <RefreshButton isWorking={isPriceLoading} onClick={handleOpen} disabled={isPriceLoading || disabled} />
                        </span>
                    }
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isPriceLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                    helperText={
                        selectedItem
                            && Number.isFinite(selectedItem.quantity)
                            && Number.isFinite(selectedItem.price)
                            ? t('common:ui.labels.totalWithValue', {
                                value: Util.formatNumber(selectedItem.total),
                            })
                            : undefined
                    }
                />
            )}
        />
    );
};

export default AsynchronousPrice;
